import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { SecondaryMenuItemTitle } from './SecondaryMenuTitles';

type NumInputBoxTwoColProps = {
  title?: string;
  ariaLabel?: string;
  onChange?: (value: number) => void;
  onBlur?: (value: number) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  inputValue: number;
  disabled?: boolean;
  testId?: string;
  limitedNum?: number | null;
  disableNegativeNum?: boolean;
};

export const NumInputBoxTwoCol = ({
  title = 'title',
  inputValue = 0,
  onChange,
  onBlur,
  onFocus,
  testId,
  ariaLabel = 'px',
  limitedNum = null,
  disableNegativeNum = false,
  disabled = false,
}: NumInputBoxTwoColProps) => {
  const inputBoxSX = {
    width: '84px',
    height: '40px',
  };

  const containerSX = {
    width: '230px',
    height: '40px',
  };
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const validateNumber = (num: number): number | undefined => {
    if (!isMountedRef.current) return undefined;
    if (Number.isNaN(num)) return 0;

    let validated = num;
    if (disableNegativeNum) validated = Math.max(0, validated);
    if (limitedNum !== null) validated = Math.min(validated, limitedNum);
    return validated;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const num = parseFloat(rawValue) || 0;
    const validatedNum = validateNumber(num);

    if (validatedNum === undefined) return;
    onChange?.(validatedNum);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const num = parseFloat(rawValue) || 0;
    const validatedNum = validateNumber(num);

    if (validatedNum === undefined) return;
    onBlur?.(validatedNum);
  };

  return (
    <Grid data-testid={testId} container alignItems="center" justifyContent="space-between" sx={containerSX}>
      <SecondaryMenuItemTitle>{title}</SecondaryMenuItemTitle>
      <Grid item sx={inputBoxSX}>
        <OutlinedInput
          disabled={disabled}
          color="secondary"
          size="small"
          endAdornment={<InputAdornment position="end">{ariaLabel}</InputAdornment>}
          inputProps={{
            'aria-label': ariaLabel,
            type: 'number',
            step: 'any',
            min: 0,
            max: limitedNum,
          }}
          sx={{
            '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          }}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={onFocus}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          value={inputValue.toString()}
        />
      </Grid>
    </Grid>
  );
};
